import { finishPreviewGeometry } from './print-preview-geometry.js';
import { frameColour } from './paper-finish-state.js';

class ProductFormatPreview extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.productId = this.dataset.productId;
    this.scope = this.closest('.shopify-section, dialog');
    const options = { signal: this.controller.signal };
    this.addEventListener('click', this.handlePreviewClick, options);
    this.scope?.addEventListener('variant:update', this.update, options);
    this.update();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.getPicker()?.classList.remove('cp-format-controls-ready');
  }

  getPicker() {
    return this.scope?.querySelector(`variant-picker[data-product-id="${this.productId}"]`);
  }

  normalise(value) {
    const key = String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    if (key.includes('unframed')) return 'unframed';
    if (key.includes('canvas')) return 'canvas';
    if (key.includes('framed')) return 'framed';
    return key.replace(/^-|-$/g, '');
  }

  handlePreviewClick = (event) => {
    const colourButton = event.target.closest('[data-frame-colour-preview]');
    if (colourButton) {
      if (colourButton.disabled) return;
      const radio = [...(this.getPicker()?.querySelectorAll('[data-frame-colour]') || [])].find(
        (item) => item.value === colourButton.dataset.frameColourPreview && !item.disabled
      );
      radio?.click();
      return;
    }
    const button = event.target.closest('[data-format-preview]');
    if (!button || button.disabled) return;
    const group = this.getPicker()?.querySelector('[data-option-name="format"]');
    const select = group?.querySelector('select');
    if (select) {
      const option = [...select.options].find((item) => !item.disabled && this.normalise(item.value) === button.dataset.formatPreview);
      if (!option) return;
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      const radio = [...(group?.querySelectorAll('input[type="radio"]') || [])].find(
        (item) => !item.disabled && item.getAttribute('aria-disabled') !== 'true' && this.normalise(item.value) === button.dataset.formatPreview
      );
      radio?.click();
    }
  };

  update = (event) => {
    if (event && String(event.detail?.data?.productId) !== this.productId) return;
    const picker = this.getPicker();
    if (!picker) return;
    const formatGroup = picker.querySelector('[data-option-name="format"]');
    if (!formatGroup) return;
    const selected = formatGroup.querySelector('select')?.value || formatGroup.querySelector('input:checked')?.value;
    const borderGroup = picker.querySelector('[data-option-name="border"]');
    const border = borderGroup?.querySelector('select')?.value || borderGroup?.querySelector('input:checked')?.value || '';
    const colour = picker.selectedFrameColour || frameColour(picker.querySelector('[data-option-name="paper-border"] select')?.value);
    const sizeGroup = picker.querySelector('[data-option-name="size"]');
    const size = sizeGroup?.querySelector('select')?.value || sizeGroup?.querySelector('input:checked')?.value;
    let matrix;
    try { matrix = JSON.parse(picker.querySelector('[data-variant-option-matrix]')?.textContent || 'null'); } catch { return; }
    const formatIndex = matrix?.optionNames.findIndex((name) => this.normalise(name) === 'format');
    const sizeIndex = matrix?.optionNames.findIndex((name) => this.normalise(name) === 'size');
    // The re-rendered section carries each finish's price at the newly chosen size and paper.
    const fresh = event?.detail?.data?.html;
    for (const button of this.querySelectorAll('[data-format-preview]')) {
      const key = button.dataset.formatPreview;
      const freshLabel = fresh?.querySelector(`product-format-preview[data-product-id="${this.productId}"] [data-format-preview="${key}"] > span`);
      const label = button.querySelector(':scope > span');
      if (freshLabel && label && label.innerHTML !== freshLabel.innerHTML) label.innerHTML = freshLabel.innerHTML;
      const geometry = finishPreviewGeometry(size, { unframed: 'Unframed', framed: 'Framed', canvas: 'Canvas' }[key], this.dataset.portrait === 'true', border.toLowerCase().includes('15'), colour);
      if (!geometry) return;
      const art = button.querySelector('.product-format-preview__art');
      art.dataset.frameColour = colour.toLowerCase();
      for (const [property, value] of Object.entries(geometry)) art.style.setProperty(`--preview-${property}`, `${value}%`);
      const choice = [...formatGroup.querySelectorAll('option, input[type="radio"]')].find((item) => this.normalise(item.value) === key);
      const available = choice && !choice.disabled && choice.getAttribute('aria-disabled') !== 'true';
      button.disabled = !available;
      button.classList.toggle('is-selected', this.normalise(selected) === key);
      button.setAttribute('aria-pressed', String(this.normalise(selected) === key));
      let help = button.querySelector('small');
      if (!help) { help = document.createElement('small'); button.append(help); }
      const sizes = formatIndex >= 0 && sizeIndex >= 0 ? [...new Set(matrix.variants.filter((variant) => variant.available && this.normalise(variant.options[formatIndex]) === key).map((variant) => variant.options[sizeIndex]))] : [];
      help.textContent = available ? '' : `${this.dataset.unavailableLabel}${sizes.length ? ' · ' + this.dataset.sizesLabel.replace('[sizes]', sizes.join(', ')) : ''}`;
      help.hidden = available;
    }
    const colours = this.querySelector('[data-frame-colour-options]');
    if (colours) {
      const open = this.normalise(selected) === 'framed';
      // Keep the panel laid out for its closing transition, but immediately remove
      // collapsed choices from keyboard navigation and the accessibility tree.
      colours.inert = !open;
      colours.setAttribute('aria-hidden', String(!open));
      colours.toggleAttribute('data-open', open);
      colours.hidden = false;
      for (const button of colours.querySelectorAll('[data-frame-colour-preview]')) {
        const radio = [...picker.querySelectorAll('[data-frame-colour]')].find(
          (item) => item.value === button.dataset.frameColourPreview
        );
        button.disabled = !radio || radio.disabled;
        const checked = button.dataset.frameColourPreview === colour;
        button.classList.toggle('is-selected', checked);
        button.setAttribute('aria-pressed', String(checked));
      }
    }
    // The panel heading repeats the choice: "Framed · Oak".
    const value = this.querySelector('[data-step-value]');
    const finish = { unframed: 'Unframed', framed: 'Framed', canvas: 'Canvas' }[this.normalise(selected)];
    if (value && finish) {
      const text = finish === 'Framed' && colour ? `${finish} · ${colour}` : finish;
      if (value.textContent !== text) value.textContent = text;
    }
    this.hidden = false;
    picker.classList.add('cp-format-controls-ready');
  };
}

if (!customElements.get('product-format-preview')) customElements.define('product-format-preview', ProductFormatPreview);
